import { useState, useEffect, useRef } from 'react';
import { useEquipmentSearch } from '@/equipments/presentation/hooks/useEquipmentSearch';
import type { CrewField, SelectedEquipment } from '@/raptorjob/presentation/components/RaptorJobCreatePage/RaptorJobCreateModal';

export interface ResourcesFormState {
  crewFields: CrewField[];
  selectedEquipment: SelectedEquipment | null;
  selectedClass: { id?: string | number | null; name?: string | null } | null;
  selectedType: { id?: string | number | null; name?: string | null } | null;
  selectedSize: { id?: string | number | null; name?: string | null } | null;
  selectedMaxSize: { id?: string | number | null; name?: string | null } | null;
  strict: boolean;
}

export interface UseResourcesFormStateOptions {
  /**
   * Initial values for the form state
   * If provided, state will be initialized with these values
   */
  initialValues?: Partial<ResourcesFormState>;
  /**
   * Whether to reset state when initialValues change
   * Useful for edit forms where data is loaded asynchronously
   */
  resetOnInitialValuesChange?: boolean;
  /**
   * Dependencies array for reset logic
   * State will reset when any of these values change
   */
  resetDependencies?: unknown[];
}

/**
 * Shared hook for managing resources form state
 * Used by both create and edit forms to reduce duplication
 */
export const useResourcesFormState = (options: UseResourcesFormStateOptions = {}) => {
  const { initialValues, resetOnInitialValuesChange = false, resetDependencies = [] } = options;

  // Initialize state with initialValues or defaults
  const defaultState: ResourcesFormState = {
    crewFields: [],
    selectedEquipment: null,
    selectedClass: null,
    selectedType: null,
    selectedSize: null,
    selectedMaxSize: null,
    strict: false
  };

  // Compute initial state - use direct object creation since useState initializer only runs once
  // For reset logic, we use useEffect with resetDependencies
  const initialState = {
    ...defaultState,
    ...initialValues
  };

  const [crewFields, setCrewFields] = useState<CrewField[]>(initialState.crewFields);
  const [selectedEquipment, setSelectedEquipment] = useState<SelectedEquipment | null>(initialState.selectedEquipment);
  const [selectedClass, setSelectedClass] = useState<{ id?: string | number | null; name?: string | null } | null>(
    initialState.selectedClass
  );
  const [selectedType, setSelectedType] = useState<{ id?: string | number | null; name?: string | null } | null>(initialState.selectedType);
  const [selectedSize, setSelectedSize] = useState<{ id?: string | number | null; name?: string | null } | null>(initialState.selectedSize);
  const [selectedMaxSize, setSelectedMaxSize] = useState<{ id?: string | number | null; name?: string | null } | null>(
    initialState.selectedMaxSize
  );
  const [strict, setStrict] = useState<boolean>(initialState.strict);

  // Use equipment search hook (matches create form behavior)
  const {
    searchTerm: equipmentSearchTerm,
    debouncedSearchTerm: equipmentDebouncedSearchTerm,
    setSearchTerm: setEquipmentSearchTerm
  } = useEquipmentSearch();

  // Track previous reset dependencies to detect changes
  const prevResetDepsRef = useRef(resetDependencies);

  // Reset state when resetDependencies change (for edit forms)
  useEffect(() => {
    // Check if resetDependencies have actually changed
    const depsChanged =
      prevResetDepsRef.current.length !== resetDependencies.length ||
      prevResetDepsRef.current.some((dep, index) => dep !== resetDependencies[index]);

    if (resetOnInitialValuesChange && initialValues && depsChanged) {
      const newState = {
        ...defaultState,
        ...initialValues
      };
      setCrewFields(newState.crewFields ?? []);
      setSelectedEquipment(newState.selectedEquipment ?? null);
      setSelectedClass(newState.selectedClass ?? null);
      setSelectedType(newState.selectedType ?? null);
      setSelectedSize(newState.selectedSize ?? null);
      setSelectedMaxSize(newState.selectedMaxSize ?? null);
      setStrict(newState.strict ?? false);
      setEquipmentSearchTerm('');
      prevResetDepsRef.current = resetDependencies;
    }
  }, [resetOnInitialValuesChange, initialValues, setEquipmentSearchTerm, resetDependencies]);

  return {
    crewFields,
    setCrewFields,
    selectedEquipment,
    setSelectedEquipment,
    selectedClass,
    setSelectedClass,
    selectedType,
    setSelectedType,
    selectedSize,
    setSelectedSize,
    selectedMaxSize,
    setSelectedMaxSize,
    strict,
    setStrict,
    equipmentSearchTerm,
    equipmentDebouncedSearchTerm,
    setEquipmentSearchTerm
  };
};
